import { isDefined } from '@sgme/fp';
import { Popover } from '@sgme/ui';
import type { QuoteInfo } from 'apiConnector/scheduledRun/model';
import { QuoteRepriceIcon } from 'components/shared/QuoteRepriceLink/QuoteRepriceIcon';
import { AddQuotePopup } from 'components/shared/ScheduledRun/Blotter/panel/Quotes/AddQuotePopupBody';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { EDITABLE_SCHEDULED_RUN_BLOTTER_ID, getEditableScheduledRun } from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { AppState } from 'state/state.model';

const editableScheduleRunSelector = (state: AppState) => ({
  isEditing: getEditableScheduledRun(state).type === 'updateQuotes',
  quotesToUpdate: getEditableScheduledRun(state).quotes ?? [],
  updateStatus: getEditableScheduledRun(state).updateRun,
});

const useSetQuotesToUpdate = () => {
  const dispatch = useDispatch();
  const setQuotesToUpdate = (quotes: QuoteInfo[]) => {
    dispatch(scheduledRunActions.setQuotes(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, quotes));
  };
  return setQuotesToUpdate;
};

export function QuotesTab() {
  const [width, setWidth] = useState<number>();

  const ref = useResizeObserver<HTMLDivElement>(entries => {
    setWidth(entries.at(0)?.contentRect.width);
  }, []);

  const {
    quotesToUpdate,
    isEditing,
    updateStatus: { status },
  } = useSelector(editableScheduleRunSelector);

  const setQuotesToUpdate = useSetQuotesToUpdate();

  const removeQuote = (quoteId: QuoteInfo['quoteId']) => {
    const filteredQuotes = quotesToUpdate.filter(q => q.quoteId !== quoteId);
    setQuotesToUpdate(filteredQuotes);
  };

  return (
    <>
      {quotesToUpdate.map(({ quoteId, productSubType, productType }) => (
        <QuoteRow
          key={quoteId}
          quoteId={quoteId}
          productSubType={productSubType}
          productType={productType}
          isQuotesManaged={isEditing}
          disabled={status !== 'idle'}
          onRemove={() => removeQuote(quoteId)}
        />
      ))}
      {isEditing && status !== 'loading' && (
        <div ref={ref} className="d-flex justify-content-end mt-1">
          <Popover
            style={{ padding: 0 }}
            maxWidth={width}
            contents={<AddQuotePopup className="shadow" style={{ width }} />}
          >
            <button
              className="btn btn-icon-end btn-flat btn-flat-secondary"
              id="add-quote-btn"
              disabled={status === 'success'}
            >
              <FormattedMessage id="scheduledRun.quotes.add" />
              <em className="icon">add</em>
            </button>
          </Popover>
        </div>
      )}
    </>
  );
}

interface QuoteRowProps extends QuoteInfo {
  onRemove: () => void;
  isQuotesManaged?: boolean;
  disabled?: boolean;
}
function QuoteRow({ quoteId, productType, productSubType, isQuotesManaged, onRemove, disabled }: QuoteRowProps) {
  return (
    <div className="p-3 my-2 bg-lvl2 d-flex justify-content-between align-items-center">
      <div>
        {isDefined(productType) && isDefined(productSubType) && (
          <div>
            {productType} / {productSubType}
          </div>
        )}
        <span className="text-secondary">{quoteId}</span>
      </div>
      <div className="d-flex align-items-center">
        <QuoteRepriceIcon linkClassName="mx-1" externalQuoteId={quoteId!}></QuoteRepriceIcon>
        {isQuotesManaged && (
          <button className="btn-icon btn text-secondary" onClick={onRemove} disabled={disabled}>
            <em className="icon icon-outlined icon-sm">delete</em>
          </button>
        )}
      </div>
    </div>
  );
}
